import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { InventoryImageSignedUrlResponse } from '../../types/inventory';

const getSignedUrl = async (
    sessionId: string,
): Promise<InventoryImageSignedUrlResponse> => {
    const { data } = await axios.post(
        `${API_URL}/inventory/image/get-signed-url`,
        {},
        { headers: { Authorization: sessionId } },
    );
    return data;
};

async function uploadFileToS3(signedUrl: string, file: File): Promise<void> {
    const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type,
        },
    });
    if (!response.ok) {
        throw new Error(`Failed to upload image: ${response.status}`);
    }
}

/**
 * Gets a signed upload URL and PUTs the file to S3.
 * Returns the S3 key to pass as imageKey on create/update.
 */
export async function uploadInventoryImage(
    sessionId: string,
    file: File,
): Promise<string> {
    const { signedUrl, key } = await getSignedUrl(sessionId);
    await uploadFileToS3(signedUrl, file);
    return key;
}

export const useUploadInventoryImage = (
    options?: UseMutationOptions<string, unknown, File>,
) => {
    const sessionContext = useContext(SessionContext);

    return useMutation({
        mutationFn: (file: File) =>
            uploadInventoryImage(sessionContext?.sessionId ?? '', file),
        ...options,
    });
};
